'use client';

// app/collections/CollectionsClient.tsx
import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Collection } from '@/types/collection';
import { CollectionSkeleton } from '@/components/collection/CollectionSkeleton';
import { Search, Filter, SortAsc, SortDesc, Star } from 'lucide-react';
import { 
  sortCollectionsByPriority, 
  sortCollectionsByPopularity, 
  CollectionWithPriority 
} from '@/lib/collections-utils';

interface CollectionsClientProps {
  collections: Collection[];
}

type SortOption = 'featured' | 'popular' | 'name-asc' | 'name-desc' | 'newest' | 'oldest';
type FilterOption = 'all' | 'wine' | 'spirits' | 'champagne';

export function CollectionsClient({ collections }: CollectionsClientProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('featured');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [displayCount, setDisplayCount] = useState(12);
  const [loading, setLoading] = useState(false);

  // 필터링 및 정렬된 컬렉션
  const filteredAndSortedCollections = useMemo(() => {
    let filtered = collections;

    // 검색 필터링
    if (searchTerm) {
      filtered = filtered.filter(collection =>
        collection.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (collection.description && collection.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // 카테고리 필터링
    if (filterBy !== 'all') {
      filtered = filtered.filter(collection => {
        const title = collection.title.toLowerCase();
        const description = collection.description?.toLowerCase() || '';
        
        switch (filterBy) {
          case 'wine':
            return title.includes('wine') || description.includes('wine') || 
                   title.includes('red') || title.includes('white') || title.includes('rose');
          case 'spirits':
            return title.includes('spirit') || title.includes('whiskey') || title.includes('vodka') ||
                   title.includes('gin') || title.includes('rum') || title.includes('tequila');
          case 'champagne':
            return title.includes('champagne') || title.includes('sparkling') || 
                   description.includes('champagne') || description.includes('sparkling');
          default:
            return true;
        }
      });
    }

    // 정렬
    switch (sortBy) {
      case 'featured':
        return sortCollectionsByPriority(filtered);
      case 'popular':
        return sortCollectionsByPopularity(filtered);
      case 'name-asc':
        return filtered.sort((a, b) => a.title.localeCompare(b.title));
      case 'name-desc':
        return filtered.sort((a, b) => b.title.localeCompare(a.title));
      case 'newest':
        return filtered.sort((a, b) => (b.id > a.id ? 1 : -1));
      case 'oldest':
        return filtered.sort((a, b) => (a.id > b.id ? 1 : -1));
      default:
        return sortCollectionsByPriority(filtered);
    }
  }, [collections, searchTerm, sortBy, filterBy]);

  // 무한 스크롤 처리
  const handleScroll = () => {
    if (loading) return;
    
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    if (scrollTop + windowHeight >= documentHeight - 100) {
      setLoading(true);
      setTimeout(() => {
        setDisplayCount(prev => Math.min(prev + 12, filteredAndSortedCollections.length));
        setLoading(false);
      }, 500);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [filteredAndSortedCollections.length]);

  // 필터/정렬 변경 시 표시 개수 리셋
  useEffect(() => {
    setDisplayCount(12);
  }, [searchTerm, sortBy, filterBy]);

  const displayedCollections = filteredAndSortedCollections.slice(0, displayCount);

  if (!collections || collections.length === 0) {
    return (
      <div className="container max-w-5xl mx-auto py-8">
        <h1 className="text-3xl font-bold mb-6">Collections</h1>
        <p className="text-gray-600">No collections are currently available.</p>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl mx-auto px-4 py-8">
      {/* 헤더 및 통계 */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Collections</h1>
        <p className="text-gray-600 text-sm">
          {filteredAndSortedCollections.length} of {collections.length} collection{collections.length !== 1 ? 's' : ''} available
        </p>
      </div>

      {/* 검색 및 필터 */}
      <div className="mb-6 space-y-4">
        {/* 검색바 */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search collections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* 필터 및 정렬 */}
        <div className="flex flex-wrap gap-4">
          {/* 카테고리 필터 */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value as FilterOption)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="wine">Wine</option>
              <option value="spirits">Spirits</option>
              <option value="champagne">Champagne</option>
            </select>
          </div>

          {/* 정렬 */}
          <div className="flex items-center gap-2">
            {sortBy.includes('asc') ? <SortAsc className="w-4 h-4 text-gray-500" /> : <SortDesc className="w-4 h-4 text-gray-500" />}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="featured">Featured</option>
              <option value="popular">Most Popular</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {/* 컬렉션 그리드 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6">
        {displayedCollections.map((collection) => (
          <Link
            key={collection.id}
            href={`/collections/${collection.handle}`}
            className="group relative overflow-hidden rounded-lg bg-white shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
          >
            {/* 이미지 */}
            <div className="aspect-square relative">
              {collection.image?.transformedSrc ? (
                <Image
                  src={collection.image.transformedSrc}
                  alt={collection.image.altText || collection.title}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={90}
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-gray-100">
                  <span className="text-gray-400">No image</span>
                </div>
              )}
              
              {/* 오버레이 효과 */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              
              {/* 인기 컬렉션 배지 */}
              {(collection as CollectionWithPriority).featured && (
                <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  Featured
                </div>
              )}
            </div>

            {/* 컬렉션 정보 */}
            <div className="p-4">
              <h2 className="text-lg font-semibold mb-2 group-hover:text-blue-600 transition-colors">
                {collection.title}
              </h2>
              
              {collection.description && (
                <p className="text-gray-600 text-sm line-clamp-2">
                  {collection.description}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* 로딩 인디케이터 */}
      {loading && (
        <div className="flex justify-center mt-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* 더 이상 표시할 컬렉션이 없을 때 */}
      {displayedCollections.length === filteredAndSortedCollections.length && 
       filteredAndSortedCollections.length > 0 && (
        <div className="text-center mt-8 text-gray-500">
          <p>You've reached the end of the collections</p>
        </div>
      )}

      {/* 검색 결과가 없을 때 */}
      {filteredAndSortedCollections.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No collections found matching your criteria</p>
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterBy('all');
              setSortBy('featured');
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
} 